import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, doc, getFirestore, updateDoc } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import type { FirebaseApp } from "firebase/app";
import {
  AppBar,
  type Authenticator,
  CircularProgressCenter,
  type DataSourceDelegate,
  Drawer,
  DRAWER_WIDTH,
  type Entity,
  type FetchEntityProps,
  FireCMS,
  FireCMSi18nProvider,
  type ModeController,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useApp,
  useAuthController,
  useBuildLocalConfigurationPersistence,
  useBuildNavigationController,
  useLargeLayout,
  useNavigationController,
  useTranslation,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  type FirebaseAuthController,
  FirebaseLoginView,
  type FirebaseSignInProvider,
  type FirebaseUserWrapper,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
} from "@firecms/firebase";
import { Avatar, CenteredView, CheckIcon, IconButton, LogoutIcon, Menu, MenuItem, TranslateIcon } from "@firecms/ui";
import { firebaseConfig, functionsRegion, useEmulators } from "./firebase_config";
import { productsCollection } from "./collections/catalog";
import { allOrdersCollection, orderPurgeBlocker, removedOrdersCollection, toInvoiceCollection, toShipCollection } from "./collections/orders";
import { inquiriesCollection } from "./collections/inquiries";
import { currenciesCollection, discountCodesCollection, shippingZonesCollection } from "./collections/settings";
import { zhTW } from "./locales/zh-TW";

const translations = { "zh-TW": zhTW };
const languages = [
  { locale: "en", label: "English" },
  { locale: "zh-TW", label: "繁體中文" },
  { locale: "es", label: "Español" },
  { locale: "de", label: "Deutsch" },
  { locale: "fr", label: "Français" },
  { locale: "it", label: "Italiano" },
  { locale: "hi", label: "हिन्दी" },
  { locale: "pt", label: "Português" },
] as const;

function LanguageSelector() {
  const { i18n } = useTranslation();
  const selected = languages.find(({ locale }) => locale === i18n.language);
  return (
    <Menu
      align="end"
      trigger={
        <IconButton
          className="!w-auto gap-2 px-3"
          aria-label={`Language / 語言: ${selected?.label ?? i18n.language}`}
          title="Language / 語言"
        >
          <TranslateIcon size="small" />
          <span>{selected?.label ?? i18n.language}</span>
        </IconButton>
      }
    >
      {languages.map(({ locale, label }) => (
        <MenuItem key={locale} onClick={() => { void i18n.changeLanguage(locale); }}>
          <span lang={locale} className="flex w-full items-center justify-between gap-4">
            {label}
            {i18n.language === locale && (
              <>
                <CheckIcon size="small" />
                <span className="sr-only">selected / 已選取</span>
              </>
            )}
          </span>
        </MenuItem>
      ))}
    </Menu>
  );
}

function DocumentLanguage() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

function NavigationBreadcrumbs() {
  const navigation = useNavigationController();
  const { pathname } = useLocation();
  if (!navigation.isUrlCollectionPath(pathname)) return null;

  // The breadcrumb context is private to FireCMS; resolve labels through its public navigation API.
  const routeSegments = pathname.split("/").filter(Boolean);
  const baseLength = navigation.baseCollectionPath.split("/").filter(Boolean).length;
  const segments = routeSegments.slice(baseLength).map(decodeURIComponent);
  const collectionIds: string[] = [];
  const breadcrumbs: { title: string; url: string }[] = [];
  for (let index = 0; index < segments.length; index += 2) {
    collectionIds.push(segments[index]);
    const collection = navigation.getCollectionFromPaths(collectionIds);
    if (!collection) break;
    breadcrumbs.push({
      title: collection.name,
      url: `/${routeSegments.slice(0, baseLength + index + 1).join("/")}`,
    });
    if (segments[index + 1]) {
      breadcrumbs.push({
        title: segments[index + 1],
        url: `/${routeSegments.slice(0, baseLength + index + 2).join("/")}`,
      });
    }
  }
  return (
    <nav aria-label="Breadcrumb" className="mr-8 hidden min-w-0 lg:block">
      <ol className="flex items-center gap-2 whitespace-nowrap text-sm">
        {breadcrumbs.map(({ title, url }) => (
          <li key={url} className="flex items-center gap-2">
            <span aria-hidden className="text-text-secondary">/</span>
            <Link to={url} aria-current={pathname === url ? "page" : undefined}>{title}</Link>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function AccountMenu() {
  const authController = useAuthController();
  const navigation = useNavigationController();
  const { t } = useTranslation();
  const user = authController.user;
  if (!user) return null;
  const initial = (user.displayName?.[0] ?? user.email?.[0] ?? "A").toUpperCase();
  return (
    <Menu
      align="end"
      trigger={
        <Avatar
          aria-label={`Account / 帳號: ${user.displayName ?? user.email ?? ""}`}
          src={user.photoURL ?? undefined}
        >
          {initial}
        </Avatar>
      }
    >
      <div className="mb-2 px-4 py-2 text-text-secondary">
        {user.displayName && <div>{user.displayName}</div>}
        {user.email && <div className="text-sm">{user.email}</div>}
      </div>
      <MenuItem onClick={async () => {
        await authController.signOut();
        navigation.navigate("/", { replace: true });
      }}>
        <LogoutIcon />
        {t("log_out")}
      </MenuItem>
    </Menu>
  );
}

function connectEmulators(_config: object, app: FirebaseApp) {
  if (!useEmulators) return;
  connectFirestoreEmulator(getFirestore(app), "127.0.0.1", 8080);
  connectAuthEmulator(getAuth(app), "http://127.0.0.1:9099", { disableWarnings: true });
  connectStorageEmulator(getStorage(app), "127.0.0.1", 9199);
  connectFunctionsEmulator(getFunctions(app, functionsRegion), "127.0.0.1", 5001);
}

function redirectOrderToCollection({ path }: FetchEntityProps, entity?: Entity): boolean {
  if (path !== "orders" || !entity) return false;
  const removed = entity.values.removed === true;
  // 共用 orders 路徑的明細會拿到同一份集合設定，必須以實際路由判別畫面。
  const removedView = window.location.pathname.split("/")[2] === removedOrdersCollection.id;
  if (removed === removedView) return false;
  const destination = removed ? removedOrdersCollection.id : allOrdersCollection.id;
  window.location.replace(`/c/${destination}/${encodeURIComponent(entity.id)}${window.location.hash}`);
  return true;
}

/** 跟前台一樣只用淺色，不提供深色模式切換 */
const lightMode: ModeController = { mode: "light", setMode: () => {} };

/** 頂部導覽列：前台的浮動玻璃膠囊，左右對齊下方的內容卡片 */
function GlassAppBar() {
  const { drawerOpen, openDrawer, closeDrawer } = useApp();
  const largeLayout = useLargeLayout();
  const navigation = useNavigationController();
  // 桌面預設展開；窄螢幕先關閉，避免選單遮住內容。仍可手動切換。
  useLayoutEffect(() => {
    if (largeLayout) openDrawer();
    else closeDrawer();
  }, [largeLayout, openDrawer, closeDrawer]);
  // 側欄收合時寬 72px；手機版左邊要讓出開選單的按鈕
  const left = largeLayout ? (drawerOpen ? DRAWER_WIDTH : 72) + 16 : 72;
  return (
    <AppBar>
      <div
        className={`glass-bar absolute top-2 right-4 flex h-12 w-auto max-w-full items-center gap-2 overflow-x-auto no-scrollbar rounded-full pl-5 pr-2 transition-all ${largeLayout ? "z-10" : ""}`}
        style={{ left }}
      >
        <Link to={navigation.basePath} className="mr-2 hidden lg:block">
          <span className="brand">GameToGo<small>後台</small></span>
        </Link>
        <NavigationBreadcrumbs />
        <div className="flex-grow" />
        <LanguageSelector />
        <AccountMenu />
      </div>
    </AppBar>
  );
}
// Scaffold 用 componentType 認出導覽列
GlassAppBar.componentType = "AppBar";

export default function App() {
  return (
    <FireCMSi18nProvider translations={translations}>
      <DocumentLanguage />
      <AdminApp />
    </FireCMSi18nProvider>
  );
}

function AdminApp() {
  /** 只有 custom claims 裡有 admin 或 staff 的帳號可以登入 */
  const authenticator: Authenticator<FirebaseUserWrapper> = useCallback(async ({ user, authController }) => {
    const token = await user?.firebaseUser?.getIdTokenResult(true);
    const admin = token?.claims.admin === true;
    const staff = token?.claims.staff === true;
    if (!admin && !staff) throw Error("這個帳號沒有後台權限，請管理員用 scripts/set-role.ts 開通。");
    authController.setExtra({ admin, staff });
    return true;
  }, []);

  const collections = useMemo(
    () => [
      toShipCollection,
      toInvoiceCollection,
      allOrdersCollection,
      removedOrdersCollection,
      inquiriesCollection,
      productsCollection,
      currenciesCollection,
      shippingZonesCollection,
      discountCodesCollection,
    ],
    [],
  );

  const { firebaseApp, firebaseConfigLoading, configError } = useInitialiseFirebase({ firebaseConfig, onFirebaseInit: connectEmulators });
  const signInOptions: FirebaseSignInProvider[] = useEmulators ? ["google.com", "password"] : ["google.com"];
  const authController: FirebaseAuthController = useFirebaseAuthController({ firebaseApp, signInOptions });
  const userConfigPersistence = useBuildLocalConfigurationPersistence();
  const firestoreDelegate = useFirestoreDelegate({ firebaseApp });
  // FireCMS 的單號查詢與明細連結不套用 forceFilter，單筆讀取也必須隔離兩種畫面。
  const { fetchEntity, listenEntity, deleteEntity } = firestoreDelegate;
  const fetchVisibleEntity = useCallback<DataSourceDelegate["fetchEntity"]>(async (props) => {
    const entity = await fetchEntity(props);
    if (redirectOrderToCollection(props, entity)) return undefined;
    return entity;
  }, [fetchEntity]);
  const listenVisibleEntity = useMemo<DataSourceDelegate["listenEntity"]>(() => listenEntity ? (props) => listenEntity({
    ...props,
    onUpdate(entity) {
      if (redirectOrderToCollection(props, entity)) return;
      props.onUpdate(entity);
    },
  }) : undefined, [listenEntity]);
  // 一般訂單清單的工具列批次刪除與列選單共用軟移除；只有「已移除訂單」畫面的刪除是永久刪除（Firestore 規則限 admin）。
  // 其他集合維持原本的刪除行為。
  const deleteVisibleEntity = useCallback<DataSourceDelegate["deleteEntity"]>(async (props) => {
    if (props.entity.path !== "orders") return deleteEntity(props);
    if (props.collection?.id === removedOrdersCollection.id) {
      const blocker = orderPurgeBlocker(props.entity);
      if (blocker) throw new Error(blocker);
      return deleteEntity(props);
    }
    if (props.entity.values.removed === true) throw new Error("這筆訂單已移除，不能再次移除。");
    if (!firebaseApp) throw new Error("Firebase 尚未初始化。");
    await updateDoc(doc(getFirestore(firebaseApp), "orders", props.entity.id), { removed: true });
  }, [deleteEntity, firebaseApp]);
  const dataSourceDelegate = {
    ...firestoreDelegate,
    fetchEntity: fetchVisibleEntity,
    listenEntity: listenVisibleEntity,
    deleteEntity: deleteVisibleEntity,
  };
  const storageSource = useFirebaseStorageSource({ firebaseApp });

  const { authLoading, canAccessMainView, notAllowedError } = useValidateAuthenticator({
    authController,
    authenticator,
    dataSourceDelegate,
    storageSource,
  });

  const navigationController = useBuildNavigationController({
    disabled: authLoading,
    collections,
    authController,
    dataSourceDelegate,
  });

  if (firebaseConfigLoading || !firebaseApp) return <CircularProgressCenter />;
  if (configError) return <CenteredView>{configError}</CenteredView>;

  return (
      <SnackbarProvider>
        <ModeControllerProvider value={lightMode}>
          <FireCMS
            navigationController={navigationController}
            authController={authController}
            userConfigPersistence={userConfigPersistence}
            dataSourceDelegate={dataSourceDelegate}
            storageSource={storageSource}
          >
            {({ loading }) => {
              if (loading || authLoading) return <CircularProgressCenter size="large" />;
              if (!canAccessMainView) {
                return (
                  <div className="login-stage">
                    <div className="ambient" aria-hidden />
                    <div className="glass-bar absolute top-4 right-4 rounded-full p-1">
                      <LanguageSelector />
                    </div>
                    <FirebaseLoginView
                      className="login-card w-full max-w-[420px] min-w-0 px-8 py-10"
                      logo="/logo.png"
                      authController={authController}
                      firebaseApp={firebaseApp}
                      signInOptions={signInOptions}
                      notAllowedError={notAllowedError}
                    >
                      <h1>GameToGo 後台</h1>
                      <p>請用已開通權限的 Google 帳號登入</p>
                    </FirebaseLoginView>
                  </div>
                );
              }
              return (
                <Scaffold autoOpenDrawer={false} logo="/logo.png">
                  <GlassAppBar />
                  <Drawer />
                  <NavigationRoutes />
                  <SideDialogs />
                </Scaffold>
              );
            }}
          </FireCMS>
        </ModeControllerProvider>
      </SnackbarProvider>
  );
}
